import { Inject, Injectable } from '@angular/core';
import { CorrelationApiService, V1ComputeCorrelationMatrixRequest, V1ComputeCorrelationMatrixResponse } from '@blk/portfolio-security-server/analytics/correlation';
import { ModelAnalyticsAPIComputeModelFamilyAnalyticsRequest, ModelAnalyticsApiService } from '@blk/portfolio-security-server/analytics/model';
import { Distributionanalyticsv1PortfolioAnalyticsResponse, PortfolioAnalyticsApiService } from '@blk/portfolio-security-server/analytics/portfolio-analytics';
import { AssetApiService, V1FacetResult, V1SearchAssetsRequest } from '@blk/portfolio-security-server/asset';
import { ModelsApiService, V1ListModelFamilyRequest, Wealthdistributionmodelsv1BatchGetModelsRequest, Wealthdistributionmodelsv1BatchGetModelsResponse } from '@blk/portfolio-security-server/models';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { AppStoreConfig, PasFeature, WwePortfolioAnalysisServiceParams } from '@ddt-web/data-service';
import { ErrorObject, PasError } from '@ddt-web/error-handler';
import { InjectQueryParam } from '@ddt-web/generic-utils';
import { StoreService } from '@ddt-web/store-service';
import { SharedQueryParamKey } from '@ddt-web/url';
import { WweSecurityDetailsParams } from '@ddt-web/wealth-web';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { mapSecurityRequestToAssetResolveRequest } from '../domain/requests';
import { mapPortfolioAnalysisParamsToDistributionanalyticsv1ComputePortfolioAnalyticsRequest } from '../domain/requests/portfolio-analytics-request-mapping';
import { AssetResolveResponse, mapAssetResolveResponse, mapModelsFamiliesResponse, mapMultiResolveSecurityDetailsResponseBodyToMultiResolveSecurityDetailsResponse, mapSecuritySearchResponseToFacetFieldsList, mapSecuritySearchResponseToSecurityList, ModelFamilyResponse, SecuritySearchResponse } from '../domain/responses';
import { MultiResolveSecurityDetailsResponse } from '../domain/responses/multi-resolve-security-details-response.model';
import { DataConversionUtil } from '../utils';

@Injectable({
	providedIn: 'root'
})
export class PasApiService {
	public static readonly POSITION_DETAILS_CONFIG_NAME = 'positionDetails';
	public static readonly TAX_DETAILS_CONFIG_NAME = 'taxDetails';
	public static readonly COMPARE_CONFIG_NAME = 'positionDetailsCompare';
	public static readonly TRANSIENT_POSITION_DETAILS_CONFIG_NAME = 'positionDetailsTransientAnalysis';
	public readonly CORRELATION_MATRIX: string = 'correlation-matrix';
	@InjectQueryParam(SharedQueryParamKey.PAS_PREVIEW) private readonly preview: string;

	constructor(
		private assetApiService: AssetApiService,
		private portfolioAnalyticsApiService: PortfolioAnalyticsApiService,
		private modelsApiService: ModelsApiService,
		private modelAnalyticsService: ModelAnalyticsApiService,
		private correlationMatrixApiService: CorrelationApiService,
		private storeService: StoreService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) { }

	/**
	 * resolve asset for only unambiguous identifiers
	 * @param securityDetailsParams 
	 * @returns 
	  */
	public fetchSecurityDetails(securityDetailsParams: WweSecurityDetailsParams, feature?: any, storeName?: string): Observable<AssetResolveResponse> {
		if (!securityDetailsParams?.body?.securities?.length) {
			return of({ assets: new Map() } as AssetResolveResponse);
		}

		return this.assetApiService.assetAPIResolveAssets(
			uuid(),
			new Date().toISOString(),
			mapSecurityRequestToAssetResolveRequest(securityDetailsParams)
		).pipe(
			catchError((error: PasError) => {
				// TODO wdsDecom: adding the current(wwe) feature based error handling when api fails, update as per the tool level requirement
				if (this.storeConfig.positionDetails) {
					this.storeService.upsertMany(this.storeConfig.positionDetails, []);
				}

				if (feature === PasFeature.BUY_FUNDS) {
					return throwError(new ErrorObject(error, 'positionDetailsBuyFunds'));
				} else if (feature === PasFeature.COMPARE) {
					// this line will be hit when the compare api fail (400 or 500 code)
					return throwError(new ErrorObject(error, 'positionDetailsCompare'));
				} else if (feature === PasFeature.TRANSIENT_ANALYSIS) {
					return throwError(new ErrorObject(error, PasApiService.TRANSIENT_POSITION_DETAILS_CONFIG_NAME));
				} else {
					const store = storeName ?? PasApiService.POSITION_DETAILS_CONFIG_NAME;
					return throwError(new ErrorObject(error, store));
				}
			}),
			map(mapAssetResolveResponse)
		);
	}

	/**
	 * multi-asset resolve asset for only unambiguous identifiers
	 * @param securityDetailsParams
	 * @returns
	 */
	public fetchMultiResolveSecurityDetails(securityDetailsParams: WweSecurityDetailsParams): Observable<MultiResolveSecurityDetailsResponse> {
		return this.assetApiService.assetAPIResolveAmbiguousAssets(uuid(), new Date().toISOString(), mapSecurityRequestToAssetResolveRequest(securityDetailsParams, true)).pipe(
			// TODO wdsDecom: error handling
			map(mapMultiResolveSecurityDetailsResponseBodyToMultiResolveSecurityDetailsResponse)
		);
	}

	/**
	 * Returns the asset details of any requested asset based on the searched query object.
	 * This query object supports any searchable text supported via facets and filters to provide narrowed down search result.
	 * quota-limit: 1000
	 * @param searchSecurityParams
	 * @returns
	 */
	public securitySearch(searchSecurityRequest: V1SearchAssetsRequest): Observable<SecuritySearchResponse> {
		return this.assetApiService.assetAPISearchAssets(uuid(), new Date().toISOString(), searchSecurityRequest).pipe(
			// TODO wdsDecom: error handling
			map(mapSecuritySearchResponseToSecurityList)
		);
	}

	/**
	 * Returns the facet fields of any requested asset based on the searched query object.
	 * @param searchSecurityParams
	 * @returns
	 */
	public fetchFacets(searchSecurityRequest: V1SearchAssetsRequest): Observable<Array<V1FacetResult>> {
		return this.assetApiService.assetAPISearchAssets(uuid(), new Date().toISOString(), searchSecurityRequest).pipe(
			// TODO wdsDecom: error handling
			map(mapSecuritySearchResponseToFacetFieldsList)
		);
	}

	public fetchPortfolioAnalysis(apiParams: WwePortfolioAnalysisServiceParams): Observable<Distributionanalyticsv1PortfolioAnalyticsResponse> {
		// TEMP: Override if you don't have rcs - for testing purposes.

		// This works for testc TPM data which won't work without override
		// apiParams.rc = null;
		// apiParams.date = '2023-03-31';
		// apiParams.analytics = [
		// 	'PORTFOLIO_ANALYTIC_TYPE_SIMULATED_PERFORMANCE',
		// 	'PORTFOLIO_ANALYTIC_TYPE_RISK',
		// 	'PORTFOLIO_ANALYTIC_TYPE_SCENARIOS',
		// 	'PORTFOLIO_ANALYTIC_TYPE_RISK_FACTOR_CONTRIB',
		// 	'PORTFOLIO_ANALYTIC_TYPE_RISK_POSITION_CONTRIB',
		// 	'PORTFOLIO_ANALYTIC_TYPE_RISK_FACTOR_POSITION_CONTRIB',
		// 	'PORTFOLIO_ANALYTIC_TYPE_IDIO_POSITION_CONTRIB',
		// 	'PORTFOLIO_ANALYTIC_TYPE_ESG',
		// 	'PORTFOLIO_ANALYTIC_TYPE_CUSTOM_ANALYTICS',
		// 	'PORTFOLIO_ANALYTIC_TYPE_BREAKDOWNS',
		// 	'PORTFOLIO_ANALYTIC_TYPE_NAV',
		// 	'PORTFOLIO_ANALYTIC_TYPE_CHARACTERISTICS',
		// 	'PORTFOLIO_ANALYTIC_TYPE_CUSTOM_ANALYTICS'
		// ];
		// apiParams.pkg = 'AC';

		const apiParamsComputePortfolios = mapPortfolioAnalysisParamsToDistributionanalyticsv1ComputePortfolioAnalyticsRequest(apiParams);
		if (this.preview === 'true') {
			apiParamsComputePortfolios.preview = true;
		}
		return this.portfolioAnalyticsApiService.portfolioAnalyticsAPIComputePortfolioAnalytics(
			uuid(),
			new Date().toISOString(),
			apiParamsComputePortfolios
		).pipe(
			map(responseJson => {
				responseJson.portfolioAnalytics.forEach((portfolioAnalytic) => {
					if (portfolioAnalytic.totalPortfolioAnalysis?.ruleEvaluations) {
						const ruleEvaluations = DataConversionUtil.convertArrayToObjectMapping(portfolioAnalytic.totalPortfolioAnalysis, 'name', 'ruleEvaluations');
						portfolioAnalytic.totalPortfolioAnalysis.ruleEvaluations = ruleEvaluations as any;
					}
				});
				return responseJson;
			})
		);
		// TODO wdsDecom: error handling
	}
	/**
	 *
	 * @param requestBody
	 * @returns
	 */
	public fetchModelFamilies(requestParams: V1ListModelFamilyRequest): Observable<ModelFamilyResponse> {
		const preview = this.preview === 'true';
		return this.modelsApiService.modelAPIListModelFamily(uuid(), new Date().toISOString(), { ...requestParams, preview }).pipe(map(mapModelsFamiliesResponse));
		// TODO wdsDecom: error handling
	}

	/**
		 * 
		 * @param requestParams 
		 * @returns 
		 */
	public fetchModelHoldings(requestParams: Wealthdistributionmodelsv1BatchGetModelsRequest): Observable<Wealthdistributionmodelsv1BatchGetModelsResponse> {
		const preview = this.preview === 'true';
		return this.modelsApiService.modelAPIBatchGetModels(
			uuid(),
			new Date().toISOString(),
			{ ...requestParams, preview }
		).pipe(map((response: Wealthdistributionmodelsv1BatchGetModelsResponse) => {
			// console the error if the api is success but error code is present
			if (response.error?.code) {
				console.error('Models BatchGet - No data available', response.error);
			}
			return response;
		}));
	}
	/**
	 *
	 * @param familyId
	 * @param requestParams
	 * @returns
	 */
	public fetchModelsAnalytics(familyId: string, requestParams: ModelAnalyticsAPIComputeModelFamilyAnalyticsRequest) {
		return this.modelAnalyticsService.modelAnalyticsAPIComputeModelFamilyAnalytics(uuid(), new Date().toISOString(), familyId, requestParams);
		// TODO wdsDecom: error handling
	}

	/**
	 * fetches correlation matrix for given assetIds
	 * 
	 * @param requestParams V1ComputeCorrelationMatrixRequest
	 * @returns Observable<V1ComputeCorrelationMatrixResponse>
	 */
	public fetchCorrelationMatrix(requestParams: V1ComputeCorrelationMatrixRequest): Observable<V1ComputeCorrelationMatrixResponse> {
		return this.correlationMatrixApiService.correlationMatrixAPIComputeCorrelationMatrix(
			uuid(),
			new Date().toISOString(),
			requestParams
		).pipe(catchError((error) => {
			this.storeService.upsertMany(this.storeConfig.correlationMatrix, []);
			return throwError(new ErrorObject(error, this.CORRELATION_MATRIX));
		}));
	}
}
